import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"
import {
  getRefreshToken,
  removeRefreshToken,
  setRefreshToken,
} from "./refreshTokenStorage"
import jwtDecode from "./jwtDecode"
import type { TokenResponse } from "./model"
import { clearAbly } from "@/base/ably"
import { trackUser } from "@/monitoring"
import type { Uuid } from "@/utils"

export type State = {
  isLoading: boolean
  userId: Uuid | null
  roles: string[]
  token: string | null
  isUnverifiedExpired?: true
  tenantSlug: string | null
}

const initialState: State = {
  isLoading: true,
  userId: null,
  roles: [],
  token: null,
  tenantSlug: null,
}

const endSession = (state: State) => {
  state.userId = null
  state.roles = []
  state.token = null
  state.tenantSlug = null
  state.isLoading = false
  delete state.isUnverifiedExpired

  clearAbly()
}

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateTokens(
      state,
      {
        payload: { token, refreshToken, isUnverifiedExpired },
      }: PayloadAction<TokenResponse>,
    ) {
      const { userId, roles } = jwtDecode(token)

      state.userId = userId
      state.roles = roles
      state.token = token
      state.isLoading = false
      if (isUnverifiedExpired) {
        state.isUnverifiedExpired = true
      }

      setRefreshToken(refreshToken)

      void trackUser(userId)
    },
    /**
     * Ends the session and forgets the stored refresh token.
     *
     * The payload, where there is one, is the token this session was actually using. The store is
     * shared between tabs and last-writer-wins, so a request that started before another tab
     * replaced the token must not delete what that tab stored — its own token being dead says
     * nothing about the newer one. Dispatched without a payload (a deliberate sign-out) it clears
     * whatever is there.
     */
    clearAuth(
      state,
      { payload: usedToken }: PayloadAction<string | undefined>,
    ) {
      endSession(state)

      if (usedToken === undefined || getRefreshToken() === usedToken) {
        removeRefreshToken()
      }
    },
    /**
     * Ends the session but keeps the stored refresh token, for when the server declined to answer
     * rather than rejecting the credential — a rate limit, a dropped connection. Throwing away a
     * token that is still good turns a moment's trouble into a login nobody needed.
     */
    suspendAuth(state) {
      endSession(state)
    },
    setTenantSlug(state, action: PayloadAction<string>) {
      state.tenantSlug = action.payload
    },
  },
  selectors: {
    getToken: (state) => state.token,
    getTokenAvailable: (state) => !!state.token,
    getLoading: (state) => state.isLoading,
    getUnverifiedExpired: (state) => state.isUnverifiedExpired,
    getUserId: (state) => state.userId,
    getRoles: (state) => state.roles,
    getTenantSlug: (state) => state.tenantSlug,
  },
})

export const { updateTokens, clearAuth, suspendAuth, setTenantSlug } =
  slice.actions
export const {
  getToken,
  getTokenAvailable,
  getLoading,
  getUserId,
  getRoles,
  getUnverifiedExpired,
  getTenantSlug,
} = slice.selectors

export default slice
